/**
 * AST to Markdown Renderer
 * Converts AST structures to clean markdown for LLM operations
 */

import {
  Block,
  ContentAST,
  DividerBlock,
  EncryptedNode,
  HeadingBlock,
  ImageBlock,
  InlineContent,
  LinkNode,
  ListBlock,
  ListItem,
  Mark,
  ParagraphBlock,
  QuoteBlock,
  TextNode,
} from './astTypes'
import { isInlineEmbedReference, serializeEmbedReference } from './embedReference'
import { MARK_FORMATS } from './markFormats'
import { stripWritingNotes } from './writingSyntax'

export interface MarkdownRendererOptions {
  includeWritingNotes?: boolean
  mode?: 'author' | 'reader' // Affects encryption content visibility
  includeMetadata?: boolean // Whether to include frontmatter
  lineBreaks?: 'single' | 'double' // Line break style between blocks
  /**
   * Prefix each block with `<!-- id:… -->` so a reader can address it.
   *
   * For LLM consumption only — the assistant cannot propose a change to "this paragraph"
   * without a way to name it. Never enable this on a path whose output is parsed back or
   * written to disk: the markers add blocks the parser would have to account for. Stripped
   * from anything the model sends back by `stripBlockIdMarkers`.
   */
  blockIds?: boolean
}

/** Matches the marker emitted by the `blockIds` option. */
const BLOCK_ID_MARKER = /^[ \t]*<!--\s*id:([^\s>]+)\s*-->[ \t]*$/gm

/**
 * Renders AST to Markdown
 */
export class MarkdownRenderer {
  private options: Required<MarkdownRendererOptions>

  constructor(options: MarkdownRendererOptions = {}) {
    this.options = {
      mode: 'author',
      includeWritingNotes: options.mode !== 'reader',
      includeMetadata: true,
      lineBreaks: 'double',
      blockIds: false,
      ...options,
    }
  }

  /**
   * Renders a complete AST to Markdown
   */
  public render(ast: ContentAST): string {
    const parts: string[] = []

    // Add frontmatter if requested
    if (this.options.includeMetadata) {
      const frontmatter = this.renderFrontmatter(ast)
      if (frontmatter) {
        parts.push(frontmatter)
      }
    }

    // Render blocks (empty paragraphs become <!-- empty --> markers)
    const sourceBlocks = this.options.includeWritingNotes ? ast.blocks : stripWritingNotes(ast.blocks, false, true)
    const blocks = sourceBlocks.map((block) => {
      const rendered = this.renderBlock(block)
      return this.options.blockIds ? `<!-- id:${block.id} -->\n${rendered}` : rendered
    })
    parts.push(...blocks)

    // Join with appropriate line breaks
    const separator = this.options.lineBreaks === 'double' ? '\n\n' : '\n'
    return parts.join(separator)
  }

  /**
   * Renders frontmatter metadata
   */
  private renderFrontmatter(ast: ContentAST): string {
    const metadata: string[] = []

    metadata.push(`lang: ${ast.meta.lang}`)
    metadata.push(`dir: ${ast.meta.dir}`)

    if (ast.meta.title) {
      metadata.push(`title: '${ast.meta.title.replace(/'/g, "''")}'`)
    }

    return `---\n${metadata.join('\n')}\n---`
  }

  /**
   * Renders a single block to Markdown
   */
  public renderBlock(block: Block): string {
    switch (block.type) {
      case 'heading':
        return this.renderHeading(block)
      case 'paragraph':
        return this.renderParagraph(block)
      case 'list':
        return this.renderList(block)
      case 'quote':
        return this.renderQuote(block)
      case 'divider':
        return this.renderDivider(block)
      case 'image':
        return this.renderImage(block)
      case 'embed':
        return `<!-- embed:${serializeEmbedReference(block.source)} -->`
      default:
        // @ts-expect-error - exhaustive check
        throw new Error(`Unknown block type: ${block.type}`)
    }
  }

  /**
   * Renders a heading block
   */
  private renderHeading(block: HeadingBlock): string {
    const level = '#'.repeat(block.level)
    const content = this.renderInlineContent(block.content)
    return `${level} ${content}`
  }

  /**
   * Renders a paragraph block
   */
  private renderParagraph(block: ParagraphBlock): string {
    const text = this.renderInlineContent(block.content)
    // A paragraph with only whitespace/soft-breaks is effectively empty
    return text.trim() ? text : '<!-- empty -->'
  }

  /**
   * Renders a list block
   */
  private renderList(block: ListBlock): string {
    return block.items
      .map((item, index) => {
        return this.renderListItem(item, block.ordered, index + 1, 0)
      })
      .join('\n')
  }

  /**
   * Renders a list item with proper nesting
   */
  private renderListItem(item: ListItem, ordered: boolean, index: number, depth: number): string {
    const indent = '  '.repeat(depth)
    const marker = ordered ? `${index}.` : '-'
    const content = this.renderInlineContent(item.content)

    // An empty item is written as a bare marker — no trailing space to be
    // stripped by an editor or a formatter on the way back in.
    let result = content ? `${indent}${marker} ${content}` : `${indent}${marker}`

    if (item.children && item.children.length > 0) {
      const childItems = item.children
        .map((child, childIndex) => {
          return this.renderListItem(child, item.childrenOrdered === true, childIndex + 1, depth + 1)
        })
        .join('\n')
      result += '\n' + childItems
    }

    return result
  }

  /**
   * Renders a quote block
   */
  private renderQuote(block: QuoteBlock): string {
    const content = this.renderInlineContent(block.content)
    // Split by lines and prefix each with >
    return content
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n')
  }

  /**
   * Renders a divider block
   */
  private renderDivider(_block: DividerBlock): string {
    return '---'
  }

  /**
   * Renders an image block
   */
  private renderImage(block: ImageBlock): string {
    // Markdown image syntax: ![alt](url "title")
    const alt = block.alt || 'image'
    return `![${alt}](${block.url})`
  }

  /**
   * Renders inline content to Markdown
   */
  private renderInlineContent(content: InlineContent[]): string {
    return content
      .map((node) => {
        if (node.type === 'text') {
          return this.renderTextNode(node)
        } else if (node.type === 'encrypted') {
          return this.renderEncryptedNode(node)
        } else if (node.type === 'link') {
          return this.renderLinkNode(node)
        } else if (node.type === 'inline-embed') {
          if (!isInlineEmbedReference(node.source)) {
            throw new Error('Invalid inline embed reference')
          }
          return `<!-- inline-embed:${serializeEmbedReference(node.source)} -->`
        }
        return ''
      })
      .join('')
  }

  /**
   * Renders a link node
   */
  private renderLinkNode(node: LinkNode): string {
    const text = this.renderInlineContent(node.content)
    return `[${text}](${node.url})`
  }

  /**
   * Renders a text node with formatting
   */
  private renderTextNode(node: TextNode): string {
    // Replace \n (from Lexical LineBreakNodes) with markdown soft line breaks (two trailing spaces + newline).
    // This preserves line breaks within a block through the markdown round-trip.
    const text = node.text.replace(/\n/g, '  \n')

    if (!node.marks || node.marks.length === 0) {
      return this.escapeMarkdown(text)
    }

    // Apply marks from inside to outside (reverse order)
    const reversedMarks = [...node.marks].reverse()
    const formattedText = reversedMarks.reduce((html, mark) => {
      return this.applyMarkdownMark(html, mark)
    }, text)

    return formattedText
  }

  /**
   * Renders an encrypted node
   */
  private renderEncryptedNode(node: EncryptedNode): string {
    // Consent attributes are optional and trailing, so a node without them renders
    // byte-identical to how it always has.
    const consent =
      (node.character ? ` character="${node.character}"` : '') +
      (node.authorHeld ? ' authorHeld="1"' : '') +
      (node.released ? ' released="1"' : '')

    const open = `[[encrypted id="${node.id}" algo="${node.algo}" v="${node.version}"${consent}]]`
    if (this.options.mode === 'reader') {
      // In reader mode, show encrypted placeholder with metadata only
      return `${open}[ENCRYPTED][[/encrypted]]`
    }
    // In author mode, show the actual content
    return `${open}${this.renderInlineContent(node.content)}[[/encrypted]]`
  }

  /**
   * Applies a formatting mark to Markdown text
   */
  private applyMarkdownMark(text: string, mark: Mark): string {
    const entry = MARK_FORMATS.find((f) => f.matchMark(mark))
    if (entry) {
      return entry.render(text, mark)
    }
    return text
  }

  /**
   * Escapes special Markdown characters
   */
  private escapeMarkdown(text: string): string {
    // Keep text as close as possible to what the user typed.
    // Only escape characters that can break our own inline mark parsing.
    // `*` must be escaped: a bare literal asterisk (e.g. the author's name-marker
    // "מאסק*") would otherwise be re-paired as italic/bold on the next parse.
    return text.replace(/\\/g, '\\\\').replace(/_/g, '\\_').replace(/\*/g, '\\*')
  }
}

/**
 * Convenience function to render AST to Markdown
 */
export const astToMarkdown = (ast: ContentAST, options?: MarkdownRendererOptions): string => {
  const renderer = new MarkdownRenderer(options)
  return renderer.render(ast)
}

/**
 * Convenience function to render a single block to Markdown
 */
export const blockToMarkdown = (block: Block, options?: MarkdownRendererOptions): string => {
  const renderer = new MarkdownRenderer(options)
  return renderer.renderBlock(block)
}

export interface LlmMarkdownOptions {
  includeEncrypted?: boolean
  /** Emit `<!-- id:… -->` before each block so the model can address one. */
  blockIds?: boolean
  simplifyFormatting?: boolean
  preserveStructure?: boolean
  includeMetadata?: boolean
  lineBreaks?: 'single' | 'double'
}

/**
 * Renders AST to clean markdown for LLM operations (author mode with metadata)
 */
export const astToLLMMarkdown = (ast: ContentAST, options: LlmMarkdownOptions = {}): string => {
  return astToMarkdown(ast, {
    mode: options.includeEncrypted ? 'author' : 'reader',
    includeWritingNotes: true,
    includeMetadata: options.includeMetadata ?? true,
    lineBreaks: options.lineBreaks ?? 'double',
    blockIds: options.blockIds ?? false,
  })
}

/**
 * Remove `<!-- id:… -->` markers from text the model produced.
 *
 * The model reads markdown carrying these markers, so it will sometimes echo them back in a
 * proposal. Parsing them would turn each into content the author never wrote.
 */
export function stripBlockIdMarkers(markdown: string): string {
  return markdown
    .replace(BLOCK_ID_MARKER, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Renders AST to reader markdown (encrypted content hidden)
 */
export const astToReaderMarkdown = (ast: ContentAST): string => {
  return astToMarkdown(ast, {
    mode: 'reader',
    includeMetadata: true,
    lineBreaks: 'double',
  })
}
